
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { ApiResponseData, BaseService, SupportResourceService, ICountry, ICurrency, ISupportResource, IPlatform, SanitizedResponse } from "../../../../../services";
import { NavLink, useSearchParams } from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button } from "@rivtn/noseur";

export interface ResourcesProps {
    platform: IPlatform;
}

function Resources(props: ResourcesProps) {
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const supportResourceService = SupportResourceService.getInstance();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.platform_management.management.support.resources;
    const [supportResources, setResources] = React.useState<ApiResponseData<ISupportResource>>({});
    labels.sided_labels.dropdown_form![0].control.options = [{ label: i18nManager.Labels.common.all, value: "" }, ...i18nManager.Languages];

    return SidedContainer({
        data: supportResources,
        searchParams,
        setSearchParams,
        onAction: manageSupportResource,
        queryData: fetchResources,
        labels: labels.sided_labels,
        firstStyle: {
            marginTop: 15,
            padding: "0px 20px 20px",
            border: "1px solid #F0F0F0",
        },
        secondStyle: {
            padding: 15,
            marginTop: 15,
            border: "1px solid #F0F0F0",
        },
        dataTemplate: (supportResource: ISupportResource) => {
            return (<div className="hera-item" onClick={() => {
                manageSupportResource(supportResource);
                Utils.updateSearchParams("adi", supportResource.external_id, setSearchParams);
            }}>
                <div>
                <div className="content lined-limit">{supportResource.description}</div>
                    <div className="title lined-limit"><span>{i18nManager.Languages.find((l) => l.value == supportResource.language)?.label} ({supportResource.language}) - {labels.type_labels[supportResource.type]} - </span> {supportResource.url_location}</div>
                </div>
            </div>);
        },
    } as any);

    function fetchResources() {
        supportResourceService.querySupportResources(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ISupportResource>) => {
            setResources(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageSupportResource(supportResource: ISupportResource = {} as any) {
        const dialogLabels = labels.dialogs.add_edit;
        const isUpdate = !!supportResource.description;
        supportResource.language = supportResource.language ?? searchParams.get("language");

        const dialog = ConfirmDialog({
            closable: true,
            title: (isUpdate ? dialogLabels.edit_title : dialogLabels.add_title),
            confirmLabel: (isUpdate ? dialogLabels.update_action_label : dialogLabels.add_action_label),
            style: { minWidth: 500, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm(dialogLabels.form, (name: string, value: any, searching: any) => {
                    dialogErrorRef.current!.innerText = " ";
                    (supportResource as any)[name] = value;
                    return false;
                }, supportResource, undefined, { marginTop: 0 }, {
                    "language": i18nManager.Languages
                })}
                <span className="link" style={{ marginTop: 15, width: "fit-content" }} onClick={() => {
                    window.open(`${window.location.origin}/dashboard/administration/manage-files`, '_blank')?.focus();
                }}>{dialogLabels.upload_file}</span>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            footerElements: (isUpdate ? (<Button scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 30px" }} onClick={() => {
                dialog.hide(() => deleteSupportResource(supportResource));
            }} />) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(supportResource).length < 3) {
                    dialogErrorRef.current!.innerText = dialogLabels.fill_required_fields;
                    return false;
                }
                button?.setLoadingState(true);
                supportResource.platform_id = props.platform.platform_id;
                const method = (isUpdate ? supportResourceService.updateSupportResource : supportResourceService.createSupportResource).bind(supportResourceService);
                method(supportResource).then(({ }: SanitizedResponse<ISupportResource>) => {
                    Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                    dialog.destroy(fetchResources);
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function deleteSupportResource(supportResource: ISupportResource) {
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialogs.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialogs.delete.desc.replace("${description}", supportResource.description)
                .replace("${language}", supportResource.language),
            onCancel: () => {
                dialog.hide(() => manageSupportResource(supportResource));
            },
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                supportResourceService.deleteSupportResource(supportResource.external_id).then(({ }: SanitizedResponse<any>) => {
                    Siren.alert(labels.dialogs.delete.success);
                    dialog.hide(fetchResources);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Resources;
